import type {SxProps} from "@mui/system";
import React, {forwardRef, MouseEventHandler, useLayoutEffect, useState} from 'react';
import Avatar from '@mui/joy/Avatar';
import classNames from "classnames";
import {useUserState, useDsManager} from "@stores/AppStateContext";

import {ClientUser, Workspace} from "../../../API";
import {WorkspaceModelType} from "../../../stores/WorkspaceStore";

import {getAbbreviate} from "../../../utils/userHelper";
import {workspaceName} from "../../../utils/workspaceName";

import {useFileProvider} from "../../../stores/AppStateContext";

import './Avatar.css';

export type AvatarSize = 24 | 28 | 32 | 36 | 40 | 42 | 48 | 108 | 'scale' | null;

export type BGAvatarProps = {
    color: string;
    abbreviation: string;
    alt: string;
    className?: string;
    src?: string;
    sx?: SxProps;
    onClick?: MouseEventHandler;
    waitForSrc?: boolean;
    forceUpdate?: number;
    size?: AvatarSize
};

export const BGAvatar = React.forwardRef((
    {
        color,
        abbreviation,
        alt,
        className,
        sx,
        src,
        forceUpdate,
        waitForSrc = false,
        onClick,
        size = 36,
    }: BGAvatarProps,
    ref
) => {
    const [srcFailed, setSrcFailed] = useState(false);
    useLayoutEffect(() => {
        setSrcFailed(false);
    }, [src]);
    // When the image fails to load (404 etc.) Joy falls back to the abbreviation,
    // which must not stay transparent-on-transparent.
    const showImage = !!src && !srcFailed;
    const sxAddition = {
        bgcolor: showImage || waitForSrc ? 'transparent' : color,
        color: showImage || waitForSrc ? 'transparent' : 'white',
    };
    /* @ts-ignore */
    return <Avatar
        ref={ref}
        key={forceUpdate}
        className={classNames({
            'Avatar': true,
            [className!]: className,
            [`Avatar--${size}`]: size,
        })}
        src={srcFailed ? undefined : src}
        slotProps={{img: {onError: () => setSrcFailed(true)}}}
        alt={alt}
        sx={sx ? {...sx, ...sxAddition} : sxAddition}
        children={abbreviation}
        onClick={onClick}
    />;
});

export type UserAvatarProps = {
    user: ClientUser;
    className?: string;
    presignedUrl?: string;
    sx?: SxProps;
    onClick?: MouseEventHandler;
    size?: AvatarSize;
};

export const UserAvatar = React.forwardRef((
    {
        user, size = 36, presignedUrl, ...props
    }: UserAvatarProps,
    ref
) => {
    const userState = useUserState();

    const fileProvider = useFileProvider();
    const [_presignedUrl, setPresignedUrl] = useState<string | undefined>(presignedUrl);
    useLayoutEffect(() => {
        if (presignedUrl) {
            if (presignedUrl !== _presignedUrl) {
                setPresignedUrl(presignedUrl);
            }
        } else {
            if (user.avatarPath && !presignedUrl && fileProvider) {
                fileProvider.getSignedUrl(user.avatarPath).then(avatarUrl => {
                    if (avatarUrl !== undefined) {
                        setPresignedUrl(avatarUrl);
                    }
                })
            } else {
                setPresignedUrl(undefined);
            }
        }
    }, [user.avatarPath, presignedUrl]);

    return <BGAvatar
        {...props}
        color={userState?.usersDS.getUserColor(user.id) || 'gray'}
        abbreviation={getAbbreviate(user)}
        alt={user.email}
        ref={ref}
        src={_presignedUrl}
        size={size}
    />;
});

export type WorkspaceAvatarProps = Omit<BGAvatarProps, 'user' | 'color' | 'abbreviation' | 'alt'> & {
    workspace: Workspace | WorkspaceModelType;
}

export const WorkspaceAvatar = forwardRef<HTMLDivElement, WorkspaceAvatarProps>((
    {
        workspace,
        forceUpdate,
        size = 36,
        ...props
    }: WorkspaceAvatarProps,
    ref
) => {
    const dsManager = useDsManager();
    const fileProvider = useFileProvider();
    const [presignedUrl, setPresignedUrl] = useState<string | undefined>(undefined);

    useLayoutEffect(() => {
        if (workspace.avatarPath && fileProvider) {
            fileProvider.getSignedUrl(workspace.avatarPath).then(avatarUrl => {
                if (avatarUrl !== undefined) {
                    setPresignedUrl(`${avatarUrl}`);
                }
            })
        } else {
            setPresignedUrl(undefined);
        }
    }, [workspace.avatarPath, forceUpdate]);

    const wsName = workspaceName(workspace);

    return <BGAvatar
        {...props}
        sx={{borderRadius: 6}}
        color={dsManager!.workspacesDS.getWorkspaceColor(workspace.id)}
        abbreviation={wsName.charAt(0).toUpperCase()}
        alt={wsName}
        ref={ref}
        src={presignedUrl}
        size={size}
    />;
});

